import AuthenticatedTemplate from "../../components/AuthenticatedTemplate";
import React, { useState, useEffect } from "react";
import { useServicesStore } from "../../stores/options-store";
import { Link, useNavigate } from "react-router-dom";
import { LOGOS, IMAGES } from "../../utils/assets";
import { checkboxDataWelcome } from "../../data/checkboxWelcome";
import Button from "../../components/form/Button";
import ConsentDialog from "../../components/ConsentDialog";

export const Dashboard01Welcome = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const [checkboxOptions] = useState(checkboxDataWelcome);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const { services, addToServices, removeFromServices, clearServices } =
    useServicesStore();
  /**
   * useEffect to get checked items in the sessionStorage and save it on
   * setCheckedItems
   */
  useEffect(() => {
    try {
      if (services) {
        const checkboxValues = services.map((service) => service);
        setSelectedCheckboxes(checkboxValues);
      }
    } catch (error) {
      console.error("Error processing services:", error);
    }
  }, []);

  /**
   * useEffect to validate if there are checked items
   * if so, clears errorMessage state
   */
  useEffect(() => {
    if (selectedCheckboxes.length > 0) {
      setErrorMessage("");
    }
  }, [selectedCheckboxes]);

  /**
   * Function to check wich checkBox was selected and store the value in the
   * Array checkedItems
   */

  // Update checkboxHandler to add or remove each checkbox ID to/from selectedCheckboxes
  const checkboxHandler = (item) => {
    let value = item.value;
    let id = item.checkboxID;
    if (selectedCheckboxes.includes(value)) {
      removeFromServices(value);
      setSelectedCheckboxes(selectedCheckboxes.filter((val) => val !== value));
    } else {
      setSelectedCheckboxes([...selectedCheckboxes, value]);
      addToServices(value);
    }
  };
  /**
   * Function to select and unselect all checkboxes
   */
  function selectAll() {
    if (selectedCheckboxes.length === checkboxOptions.length) {
      setSelectedCheckboxes([]);
      clearServices();
    } else {
      const allValues = checkboxOptions.map((item) => {
        addToServices(item.value); // Call the function inside the map
        return item.value; // Return the value for allValues
      });
      setSelectedCheckboxes(allValues);
    }
  }
  /**
   * Function ERROR: empty checkBox to check if at least on checkbox was checked
   * If no, Error message, if yes call function to set the new page
   */
  async function isCheckBoxSelected() {
    // If no checkboxes are selected, show error and stay on the same page
    if (selectedCheckboxes.length === 0) {
      setErrorMessage("Please select at least one option to start");
      // Update the sessionStorage
      sessionStorage.setItem("LS_CHECKED_ITEMS", JSON.stringify("[]"));
      return;
    }

    // If at least one checkbox is selected, proceed to the next page
    sessionStorage.setItem(
      "LS_CHECKED_ITEMS",
      JSON.stringify(selectedCheckboxes)
    );
    setErrorMessage(""); // Clear any previous error messages

    const consentDetails = JSON.parse(
      sessionStorage.getItem("LS_CONSENT_DETAILS")
    );
    if (consentDetails && consentDetails.given === 1) {
      navigate("/dashboard-update-details");
    } else {
      setShowConsentDialog(true);
    }
  }

  function handleConsent(accepted) {
    const consentDetails = {
      given: accepted ? 1 : 0,
      version: "1.0",
      content:
        "I authorise Quickmove to act on my behalf to update my details with selected service providers.",
    };
    sessionStorage.setItem(
      "LS_CONSENT_DETAILS",
      JSON.stringify(consentDetails)
    );
    navigate("/dashboard-update-details");
  }

  return (
    <>
      <AuthenticatedTemplate>
        <ConsentDialog
          open={showConsentDialog}
          onContinue={(accepted) => handleConsent(accepted)}
        />

        <section className="step__container">
          <div className="step__content is-welcome">
            <div className="step-welcome">
              <div className="step-welcome__image">
                <img
                  src={IMAGES.WELCOME_IMAGE}
                  alt="Illustration of a man using a phone"
                />
              </div>

              <div className="step-welcome__info">
                <div className="step-welcome__wrapper">
                  <header className="step__header">
                    <img
                      className="logo"
                      src={LOGOS.QUICKMOVE_FLOW}
                      alt="Quickmove Logo"
                    />
                    <div>
                      <h1 className="text-md text-md--variant">Welcome!</h1>
                      <p className="paragraph-md">
                        Tell us what you would like to update today:
                      </p>
                    </div>
                  </header>

                  <div className="step-welcome__options">
                    {checkboxOptions
                      ? checkboxOptions.map((item, index) => (
                          <div
                            className={`step-welcome__checkbox step-welcome__checkbox${
                              selectedCheckboxes.includes(item.value) &&
                              "--active"
                            }`}
                            key={index}
                            onClick={() => checkboxHandler(item)}
                          >
                            <label className="checkbox">
                              <input
                                type="checkbox"
                                id={`checkbox-${index}`}
                                checked={selectedCheckboxes.includes(
                                  item.value
                                )}
                                onClick={(e) => e.stopPropagation()}
                                readOnly
                              />
                              <span className="checkbox__mark"></span>
                              <span className="checkbox__text">
                                {item.title}
                              </span>
                            </label>
                          </div>
                        ))
                      : null}

                    <div
                      className={`step-welcome__checkbox step-welcome__checkbox${
                        selectedCheckboxes.length === checkboxOptions.length &&
                        "--active"
                      }`}
                      onClick={selectAll}
                    >
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          id={`checkbox-select-all`}
                          checked={
                            selectedCheckboxes.length === checkboxOptions.length
                          }
                          onClick={(e) => e.stopPropagation()}
                          readOnly
                        />
                        <span className="checkbox__mark"></span>
                        <span className="checkbox__text">
                          All options above
                        </span>
                      </label>
                    </div>
                  </div>

                  {errorMessage && (
                    <p className="text-danger"> {errorMessage} </p>
                  )}

                  <div className="reminder-box">
                    <h2 className="paragraph-md semibold text-primary-950">
                      To begin your update process, you will need:
                    </h2>
                    <p className="paragraph-md text-primary-900">
                      A valid form of ID (choose one):
                    </p>
                    <ul className="reminder-box__list text-primary-900 paragraph-md">
                      <li>New Zealand Driver's Licence</li>
                      <li>New Zealand Passport</li>
                    </ul>
                    <p className="paragraph-md semibold text-primary-900">
                      For a name/gender update, you will also need one of the
                      following supporting documents:
                    </p>
                    <ul className="reminder-box__list text-primary-900 paragraph-md">
                      <li>Marriage Certificate</li>
                      <li>Re-issued Birth Certificate</li>
                      <li>Legal Name Change Certificate</li>
                    </ul>
                  </div>
                </div>
                <div className="action-buttons">
                  <Link to="/dashboard-return">
                    <Button variant="outlined">Back</Button>
                  </Link>
                  <Button
                    variant="primary"
                    onClick={() => isCheckBoxSelected()}
                  >
                    Start Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AuthenticatedTemplate>
    </>
  );
};
